import { useReducer, useEffect, useCallback } from 'react';
import { GameStateType, GameActionType, DirectionType } from '../types';
import { initializeBoard, addRandomTile, isGameOver, hasWon, WINNING_TILE } from '../lib/boardUtils';
import { moveTiles } from '../lib/moveLogic';

// Initial game state
const initialState: GameStateType = {
  board: initializeBoard(),
  score: 0,
  highScore: 0,
  isGameOver: false,
  hasWon: false,
  canContinue: false
};

// Game reducer
function gameReducer(state: GameStateType, action: GameActionType): GameStateType {
  switch (action.type) {
    case 'MOVE': {
      // If game is over or won (without continuing), ignore move
      if (state.isGameOver || (state.hasWon && !state.canContinue)) {
        return state;
      }
      
      // Move tiles in the specified direction
      const { newBoard, scoreGain, moved } = moveTiles(state.board, action.direction);
      
      // If no tiles moved, return current state
      if (!moved) {
        return state;
      }
      
      // Add a random tile to the board
      const boardWithNewTile = addRandomTile(newBoard);
      
      // Calculate new score and high score
      const newScore = state.score + scoreGain;
      const newHighScore = Math.max(state.highScore, newScore);
      
      // Check if game is over or won
      const gameOver = isGameOver(boardWithNewTile);
      const playerWon = hasWon(boardWithNewTile) && !state.hasWon;
      
      return {
        ...state,
        board: boardWithNewTile,
        score: newScore,
        highScore: newHighScore,
        isGameOver: gameOver && !playerWon,
        hasWon: state.hasWon || playerWon,
        canContinue: state.canContinue || playerWon
      };
    }
    
    case 'RESTART':
      return {
        ...initialState,
        highScore: state.highScore
      };
      
    case 'CONTINUE':
      return {
        ...state,
        canContinue: true
      };
      
    default:
      return state;
  }
}

// Custom hook for 2048 game logic
export function use2048Game() {
  const [gameState, dispatch] = useReducer(gameReducer, initialState);
  
  // Load high score from localStorage on mount
  useEffect(() => {
    const savedHighScore = localStorage.getItem('2048_high_score');
    if (savedHighScore) {
      dispatch({
        type: 'RESTART',
        ...gameState,
        highScore: parseInt(savedHighScore, 10)
      });
    }
  }, []);
  
  // Save high score to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('2048_high_score', gameState.highScore.toString());
  }, [gameState.highScore]);
  
  // Handle keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          e.preventDefault();
          dispatch({ type: 'MOVE', direction: 'UP' });
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          e.preventDefault();
          dispatch({ type: 'MOVE', direction: 'RIGHT' });
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          e.preventDefault();
          dispatch({ type: 'MOVE', direction: 'DOWN' });
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          e.preventDefault();
          dispatch({ type: 'MOVE', direction: 'LEFT' });
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Handle touch controls
  const handleTouch = useCallback((direction: DirectionType) => {
    dispatch({ type: 'MOVE', direction });
  }, []);
  
  // Restart game
  const restartGame = useCallback(() => {
    dispatch({ type: 'RESTART' });
  }, []);
  
  // Continue game after winning
  const continueGame = useCallback(() => {
    dispatch({ type: 'CONTINUE' });
  }, []);
  
  return {
    gameState,
    handleTouch,
    restartGame,
    continueGame,
    WINNING_TILE
  };
}